import { SanitizedRender } from '@reverbdotcom/commons';
import { RCTooltip } from '@reverbdotcom/cadence/components';

import AffirmLearnMoreModal from '@reverbdotcom/commons/src/components/affirm_learn_more_modal';
import React from 'react';
import I18n from 'i18n-js';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import URLHelpers from './shared/url_helpers';
import { isKlarnaPayLaterAvailable } from '../lib/klarnaExperiments';
import { RegionalKlarnaCallout } from './RegionalKlarnaCallout';
import { core_apimessages_Money as Money } from '../schemas/rql';
import experiments from '@reverbdotcom/commons/src/experiments';

interface Props {
  initialPaymentMethodSelection: PaymentMethod;
  reverbCredits: Pick<Money, 'display' | 'currency'>[];
  acceptedPaymentMethods: PaymentMethod[];
  affirmEstimatedPaymentText: string;
  affirmLearnMoreModalProps: AffirmLearnMoreModalProps;
  onPaymentMethodSelect(value: PaymentMethod): void;
  showReverbProtectionCallout: boolean;
  canUseReverbCredits: boolean;
  klarnaAccountEstimatedTotal: Pick<Money, 'amountCents' | 'currency'>;
}

const AFFIRM = 'affirm';
const DIRECT_CHECKOUT = 'direct_checkout';
const KLARNA_ACCOUNT = 'klarna_account';
const KLARNA_PAY_LATER = 'klarna_pay_later';
const PAYPAL = 'paypal';
const SOFORT = 'sofort';

type PaymentMethod =
  | typeof AFFIRM
  | typeof DIRECT_CHECKOUT
  | typeof KLARNA_ACCOUNT
  | typeof KLARNA_PAY_LATER
  | typeof PAYPAL
  | typeof SOFORT;

interface AffirmLearnMoreModalProps {
  availableTermMonths?: number[];
  buyerPrice: string;
  estimatedMonthlyPayment?: string;
  hideZeroPercentNudge?: boolean;
  maxTermMonth: number;
  zeroPercentFinancing?: boolean;
  buttonLabel?: JSX.Element | React.ReactNode | string;
  lowestAprAtMaxTerm?: number;
  splitPay: boolean;
}

function CartPaymentMethodSelector(props: Props) {
  const user = useUser();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState(props.initialPaymentMethodSelection);

  function onPaymentMethodSelect(paymentMethod: PaymentMethod) {
    setSelectedPaymentMethod(paymentMethod);
    props.onPaymentMethodSelect(paymentMethod);
  }

  const reverbCreditsInUserCurrency = props.reverbCredits.find(reverbCredit => reverbCredit.currency === user.currency);
  const inPaypalButtonRollout = useExpEnabled(experiments.PAYPAL_CHECKOUT_BUTTON);

  return (
    <div className="cart-payment-methods">
      <div className="cart-payment-methods__titleblock">
        <h2 className="cart-payment-methods__title">
          {I18n.t('discovery.paymentMethodSelection.howYoullPay')}
        </h2>
        {props.showReverbProtectionCallout &&
          <p className="cart-payment-methods__protection">
            {I18n.t('discovery.paymentMethodSelection.protection.buyWithConfidence')}
            <span className="ml-space">
              <a
                href={URLHelpers.reverbProtectionPath}
                target="_blank"
                rel="noreferrer"
                className="text-link"
              >
                {I18n.t('discovery.paymentMethodSelection.protection.protectionIncluded')}
              </a>
            </span>
          </p>
        }
      </div>
      <div className="cart-payment-methods__list">
        {props.acceptedPaymentMethods.includes(DIRECT_CHECKOUT) &&
          <div className="cart-payment-method cart-payment-method--rp">
            <PaymentMethodRadioButton
              onPaymentMethodSelect={onPaymentMethodSelect}
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethod={DIRECT_CHECKOUT}
            >
              <div>
                <div className="cart-payment-method__titleblock">
                  <h3 className="cart-payment-method__title">
                    {I18n.t('discovery.paymentMethodSelection.debitOrCreditCard')}
                  </h3>
                  <div className="cart-payment-method__icons">
                    <div className="payment-icon payment-icon--visa" role="img" aria-label={I18n.t('discovery.paymentMethodSelection.icons.visa')} />
                    <div
                      className="payment-icon payment-icon--mastercard"
                      role="img"
                      aria-label={I18n.t('discovery.paymentMethodSelection.icons.mastercard')}
                    />
                    <div
                      className="payment-icon payment-icon--discover"
                      role="img"
                      aria-label={I18n.t('discovery.paymentMethodSelection.icons.discover')} />
                    <div
                      className="payment-icon payment-icon--amex"
                      role="img"
                      aria-label={I18n.t('discovery.paymentMethodSelection.icons.amex')}
                    />
                    <div
                      className="payment-icon payment-icon--reverb-credits"
                      role="img"
                      aria-label={I18n.t('discovery.paymentMethodSelection.icons.reverbCredits')}
                    />
                  </div>
                </div>
                {props.canUseReverbCredits && !!reverbCreditsInUserCurrency &&
                  <p
                    className="cart-payment-method__credits"
                    key={`tooltip${reverbCreditsInUserCurrency.display}`}
                  >
                    <RCTooltip
                      text={I18n.t('discovery.paymentMethodSelection.creditsAppliedToEligibleItems')}
                    >
                      {
                        I18n.t('discovery.paymentMethodSelection.plusReverbCredits',
                          { display: reverbCreditsInUserCurrency.display, currency: reverbCreditsInUserCurrency.currency },
                        )}
                    </RCTooltip>
                  </p>
                }
              </div>
            </PaymentMethodRadioButton>
          </div>
        }
        {props.acceptedPaymentMethods.includes(AFFIRM) &&
          <div className="cart-payment-method">
            <PaymentMethodRadioButton
              onPaymentMethodSelect={onPaymentMethodSelect}
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethod={AFFIRM}
            >
              <div className="cart-payment-method__titleblock">
                <h3 className="cart-payment-method__title">
                  {I18n.t('discovery.paymentMethodSelection.payOverTime')}
                  {props.affirmEstimatedPaymentText != null &&
                    <div className="cart-payment-method__subtitle">
                      <SanitizedRender html={props.affirmEstimatedPaymentText} />
                      <div className="ml-space d-inline-block size-90 mobile-d-block">
                        <AffirmLearnMoreModal
                          availableTermMonths={props.affirmLearnMoreModalProps.availableTermMonths}
                          buyerPrice={props.affirmLearnMoreModalProps.buyerPrice}
                          estimatedMonthlyPayment={props.affirmLearnMoreModalProps.estimatedMonthlyPayment}
                          maxTermMonth={props.affirmLearnMoreModalProps.maxTermMonth}
                          zeroPercentFinancing={props.affirmLearnMoreModalProps.zeroPercentFinancing}
                          buttonLabel={props.affirmLearnMoreModalProps.buttonLabel}
                          lowestAprAtMaxTerm={props.affirmLearnMoreModalProps.lowestAprAtMaxTerm}
                          splitPay={props.affirmLearnMoreModalProps.splitPay}
                        />
                      </div>
                    </div>
                  }
                </h3>
                <div className="cart-payment-method__icons">
                  <div
                    className="payment-icon payment-icon--affirm"
                    role="img"
                    aria-label={I18n.t('discovery.paymentMethodSelection.icons.affirm')}
                  />
                </div>
              </div>
            </PaymentMethodRadioButton>
          </div>
        }
        {props.acceptedPaymentMethods.includes(PAYPAL) && !inPaypalButtonRollout &&
          <div className="cart-payment-method">
            <PaymentMethodRadioButton
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethod={PAYPAL}
              onPaymentMethodSelect={onPaymentMethodSelect}
            >
              <div className="cart-payment-method__titleblock">
                <h3 className="cart-payment-method__title">
                  {I18n.t('discovery.paymentMethodSelection.yourPaypalAccount')}
                </h3>
                <div className="cart-payment-method__icons">
                  <div
                    className="payment-icon payment-icon--paypal"
                    role="img"
                    aria-label={I18n.t('discovery.paymentMethodSelection.icons.paypal')}
                  />
                </div>
              </div>
            </PaymentMethodRadioButton>
          </div>
        }
        {props.acceptedPaymentMethods.includes(SOFORT) &&
          <div className="cart-payment-method">
            <PaymentMethodRadioButton
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethod={SOFORT}
              onPaymentMethodSelect={onPaymentMethodSelect}
            >
              <div className="cart-payment-method__titleblock">
                <h3 className="cart-payment-method__title">
                  {I18n.t('discovery.paymentMethodSelection.buyWithSofort')}
                </h3>
                <div className="cart-payment-method__icons">
                  <div
                    className="payment-icon payment-icon--sofort"
                    role="img"
                    aria-label={I18n.t('discovery.paymentMethodSelection.icons.sofort')}
                  />
                </div>
              </div>
            </PaymentMethodRadioButton>
          </div>
        }
        {props.acceptedPaymentMethods.includes(KLARNA_ACCOUNT) &&
          <div className="cart-payment-method">
            <PaymentMethodRadioButton
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethod={KLARNA_ACCOUNT}
              onPaymentMethodSelect={onPaymentMethodSelect}
            >
              <div className="cart-payment-method__titleblock">
                <h3 className="cart-payment-method__title">
                  {I18n.t('discovery.paymentMethodSelection.buyWithKlarnaAccount')}
                  <div className="cart-payment-method__subtitle">
                    <RegionalKlarnaCallout
                      amount={props.klarnaAccountEstimatedTotal}
                      textOnly
                    />
                  </div>
                </h3>
                <div className="cart-payment-method__icons">
                  <div
                    className="payment-icon payment-icon--klarna"
                    role="img"
                    aria-label={I18n.t('discovery.paymentMethodSelection.icons.klarna')}
                  />
                </div>
              </div>
            </PaymentMethodRadioButton>
          </div>
        }
        {isKlarnaPayLaterAvailable(props.acceptedPaymentMethods, user) &&
          <div className="cart-payment-method">
            <PaymentMethodRadioButton
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethod={KLARNA_PAY_LATER}
              onPaymentMethodSelect={onPaymentMethodSelect}
            >
              <div className="cart-payment-method__titleblock">
                <h3 className="cart-payment-method__title">
                  {I18n.t('discovery.paymentMethodSelection.buyWithKlarnaPayLater')}
                </h3>
                <div className="cart-payment-method__icons">
                  <div
                    className="payment-icon payment-icon--klarna"
                    role="img"
                    aria-label={I18n.t('discovery.paymentMethodSelection.icons.klarna')}
                  />
                </div>
              </div>
            </PaymentMethodRadioButton>
          </div>
        }
      </div>
    </div>
  );
}

function PaymentMethodRadioButton({
  selectedPaymentMethod,
  paymentMethod,
  onPaymentMethodSelect,
  children,
}: {
  selectedPaymentMethod: PaymentMethod,
  paymentMethod: PaymentMethod,
  onPaymentMethodSelect(paymentMethod: PaymentMethod): void,
  children: JSX.Element,
}) {
  const buttonID = `input_${paymentMethod}`;
  return (
    <div>
      <input
        className="cart-payment-method__input"
        type="radio"
        id={buttonID}
        value={paymentMethod}
        checked={selectedPaymentMethod === paymentMethod}
        onChange={() => onPaymentMethodSelect(paymentMethod)}
      />
      <label
        htmlFor={buttonID}
        className="cart-payment-method__label"
      >
        {children}
      </label>
    </div>
  );
}

export default CartPaymentMethodSelector;
